import React, { useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

/**
 * Modal: pick officials to SMS-notify "we have enough — you're not needed this time".
 * Candidates come from the parent (typically: those who answered but aren't assigned).
 */
export default function NotifyCompleteModal({ eventId, eventName, candidates, onClose }) {
  const [selected, setSelected] = useState(new Set(candidates.map((c) => c.id)));
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  };
  const allOn = selected.size === candidates.length;
  const toggleAll = () => {
    setSelected(new Set(allOn ? [] : candidates.map((c) => c.id)));
  };

  const submit = async () => {
    if (selected.size === 0) {
      toast.error("Sélectionne au moins un officiel");
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post(`/events/${eventId}/notify-complete`, {
        official_ids: [...selected],
        message: message.trim() || null,
      });
      const parts = [];
      if (data.sent) parts.push(`${data.sent} envoyé(s)`);
      if (data.mocked) parts.push(`${data.mocked} simulé(s)`);
      if (data.failed) parts.push(`${data.failed} échec(s)`);
      if (data.no_phone) parts.push(`${data.no_phone} sans téléphone`);
      toast.success(`SMS de complétion : ${parts.join(" · ") || "envoyés"}`);
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/65 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto">
      <div
        className="bg-[var(--surface)] border border-[var(--border-strong)] rounded-2xl p-6 max-w-xl w-full shadow-2xl my-8"
        data-testid="notify-complete-modal"
      >
        <div className="mb-4">
          <div className="label">SMS « j'ai assez de monde »</div>
          <h3 className="font-serif text-3xl leading-tight">{eventName}</h3>
          <p className="text-xs text-[var(--muted)] mt-2 leading-relaxed">
            Envoie un SMS de remerciement aux officiels qui ont répondu mais que tu n'as pas assignés.
            Ça les informe qu'ils n'ont plus besoin d'attendre l'horaire et libère ta liste.
          </p>
        </div>

        {candidates.length === 0 ? (
          <div className="border-2 border-dashed border-[var(--border-strong)] p-6 text-center">
            <p className="font-mono text-xs uppercase tracking-widest text-[var(--muted)]">
              Aucun candidat. Tous les répondants sont déjà assignés.
            </p>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-2">
              <span className="font-mono text-[10px] uppercase tracking-widest text-[var(--muted)]">
                {selected.size} sur {candidates.length} sélectionné(s)
              </span>
              <button
                type="button"
                onClick={toggleAll}
                className="font-mono text-[10px] uppercase tracking-widest text-[var(--muted)] hover:text-black"
                data-testid="notify-toggle-all"
              >
                {allOn ? "Tout désélectionner" : "Tout sélectionner"}
              </button>
            </div>
            <div className="border border-[var(--border-strong)] max-h-60 overflow-y-auto mb-4">
              {candidates.map((c) => (
                <label
                  key={c.id}
                  className="flex items-center gap-2 px-3 py-2 border-b border-[var(--border)] last:border-b-0 cursor-pointer hover:bg-[var(--surface-3)]"
                  data-testid={`notify-candidate-${c.id}`}
                >
                  <input
                    type="checkbox"
                    checked={selected.has(c.id)}
                    onChange={() => toggle(c.id)}
                    className="accent-black"
                  />
                  <span className="text-sm font-bold flex-1">
                    {c.first_name} {c.last_name}
                  </span>
                  <span className="font-mono text-[10px] text-[var(--muted)]">
                    {c.phone || "— pas de téléphone —"}
                  </span>
                </label>
              ))}
            </div>
            <div className="mb-4">
              <label className="label block mb-1.5">Message personnalisé (optionnel)</label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Laisse vide pour utiliser le message par défaut (« Merci, notre besoin est comblé… »)"
                className="input-box min-h-20 text-sm"
                maxLength={280}
                data-testid="notify-message-input"
              />
              <p className="font-mono text-[10px] uppercase tracking-widest text-[var(--muted)] mt-1 text-right">
                {message.length}/280
              </p>
            </div>
          </>
        )}

        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="btn-secondary" data-testid="notify-cancel-btn">
            Annuler
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={sending || candidates.length === 0 || selected.size === 0}
            className="btn-primary"
            data-testid="notify-send-btn"
          >
            {sending ? "Envoi…" : `Envoyer SMS (${selected.size})`}
          </button>
        </div>
      </div>
    </div>
  );
}
